import {useState} from 'react';
import {Customer, PRODUCT_IDS, PRODUCT_STATUS, ProductInstance} from '../../../util/types';
import {address, fullname, phoneNumber} from "../../../util/formatter";
import ChangePassword from "./ChangePassword";
import ChangeContactPermissions from "./ChangeContactPermissions";
import ChangeMobile from "./ChangeMobile";
import ChangeMobileConfirm from "./ChangeMobileConfirm";
import ChangeEmail from "./ChangeEmail";
import ChangeEmailConfirm from "./ChangeEmailConfirm";
import IconButton from "../IconButton";
import IconCross from "../../icons/IconCross.tsx";
import IconEdit from "../../icons/IconEdit.tsx";
import IconCheck from "../../icons/IconCheck.tsx";

function SelfcareContactPermissionsDisplay(props: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            permissions: { [index: string]: any },
            permission_text_accepted: string,
        }) {
    const no_state_properties = ['permission_marketing_text_accepted'];
    const [permissions, setPermissions] = useState<object>(props.permissions);
    const [showChangeContactPermissions, setShowChangeContactPermissions] = useState<boolean>(false)
    const [showCPermissionIcons, setShowCPermissionIcons] = useState<boolean>(true)

    Object.keys(props.permissions).map((keyName) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        props.permissions[keyName].state = useState<boolean>(props.permissions[keyName].is_state_set);
    })

    return <td className="selfcare-account-editable">
        {showCPermissionIcons && <span className="text">
        {Object.keys(props.permissions)
            .sort((a, b) => a.localeCompare(b))
            .map((keyName, index) => {
                if (!no_state_properties.includes(keyName)) {
                    return (
                        <span key={index} className="nowrap">{props.permissions[keyName].permission_type_name}
                            {props.permissions[keyName].is_action_set ? <IconCheck className='icon icon-check'/> :
                                <IconCross className='icon icon-cross'/>}
                        </span>
                    )
                }
            })}
    </span>}
        <IconButton
            className="selfcare-account-edit-link"
            component={showChangeContactPermissions ? IconCross : IconEdit}
            onClick={() => {
                setShowCPermissionIcons(!showCPermissionIcons);
                setShowChangeContactPermissions(!showChangeContactPermissions)
            }}
        />
        {showChangeContactPermissions && <ChangeContactPermissions
            permissions={permissions}
            setPermissions={setPermissions}
            permission_text_accepted={props.permission_text_accepted}
        />}
    </td>
}

function CustomerProductInstanceDisplay({customer}: { customer: Customer }) {

    if (customer.customer_type.customer_type === 'HOUSING_ASSOCIATION') {
        return <tr>
            <th>Produkter</th>
            <td className="selfcare-products">Boligforening</td>
        </tr>
    }

    const VISIBLE_PRODUCT_STATUS = [PRODUCT_STATUS.ACTIVE, PRODUCT_STATUS.PENDING];
    const visibleProductInstances = customer.product_instances
        .filter(productInstance => VISIBLE_PRODUCT_STATUS.includes(productInstance.product_instance_status))
        .filter(productInstance => productInstance.product.product_id !== PRODUCT_IDS.EXTENDER)

    const extenderProducts = customer.product_instances
        .filter(productInstance => VISIBLE_PRODUCT_STATUS.includes(productInstance.product_instance_status))
        .filter(productInstance => productInstance.product.product_id === PRODUCT_IDS.EXTENDER)

    if (visibleProductInstances.length === 0) {
        return <tr>
                <th>Produkter</th>
                <td className="selfcare-products">Ingen aktive produkter</td>
            </tr>
    }

    return <>
        <tr>
            <th>Produkter</th>
            <td className="selfcare-products">
                {visibleProductInstances.map((productInstance) =>
                    <div key={productInstance.product_instance_id} className="product-container product-container--coax">
                        <span>{productInstance.product.product_display_name}</span>
                    </div>
                )}
                {[...extenderProducts]
                    .filter((productInstance, index, self) =>
                            index === self.findIndex((t) => (
                                t.product.product_display_name === productInstance.product.product_display_name
                            ))
                    )
                    .map((productInstance) =>
                        <div key={productInstance.product_instance_id} className="product-container product-container--extender">
                            <span>{productInstance.product.product_display_name} - {extenderProducts.length} stk.</span>
                        </div>
                    )
                }
            </td>
        </tr>
        {visibleProductInstances.filter(productInstance => productInstance.circuit !== null).map((productInstance, index) => <CustomerMitWifiDisplay key={index} product_instance={productInstance} />)}
    </>
}

function CustomerMitWifiDisplay({product_instance}: { product_instance: ProductInstance }) {
    return <tr>
        <th>Login til <a href="https://mitwifi.dk" target="_blank" rel="noreferrer">mitwifi.dk</a></th>
        <td>
            <ul className="mit-wifi-credentials">
                <li>Kontonummer: <span>{product_instance.circuit?.account_no}</span></li>
                <li>Aktiveringskode: <span>{product_instance.circuit?.activation_code}</span></li>
            </ul>
        </td>
    </tr>
}

export default function AccountTable({customer}: { customer: Customer }) {
    const [mobileNo, setMobileNo] = useState<string>(customer.customer_contact.mobile_no);
    const [showChangePassword, setShowChangePassword] = useState<boolean>(false)
    const [showChangeMobile, setShowChangeMobile] = useState<boolean>(false)
    const [isChangingMobileTo, setIsChangingMobileTo] = useState<string|null>(customer.customer_contact_change.mobile_new)
    const [showChangeEmail, setShowChangeEmail] = useState<boolean>(false)
    const [isChangingEmailTo, setIsChangingEmailTo] = useState<string|null>(customer.customer_contact_change.email_new)
    const [errorMessage, setErrorMessage] = useState<string|null>(null);

    return (<>
        {errorMessage !== null && <div className="selfcare-account-error">{errorMessage}</div>}
        <table className="selfcare-account-table">
            <tbody>
            <tr>
                <th>Navn</th>
                <td>
                    <span className="text">{fullname(customer.customer_contact)}</span>
                </td>
            </tr>
            <tr>
                <th>Mobil</th>
                <td className="selfcare-account-editable">
                    <span className="text">
                        {phoneNumber(mobileNo)}
                    </span>
                    <IconButton
                        className="selfcare-account-edit-link"
                        component={showChangeMobile ? IconCross : IconEdit}
                        onClick={() => {setShowChangeMobile(!showChangeMobile); setErrorMessage(null)}}
                    />
                    {showChangeMobile && isChangingMobileTo === null && <ChangeMobile setIsChangingMobileTo={setIsChangingMobileTo}/>}
                    {showChangeMobile &&
                     isChangingMobileTo !== null &&
                     <ChangeMobileConfirm
                         isChangingMobileTo={isChangingMobileTo}
                         setIsChangingMobileTo={setIsChangingMobileTo}
                         setShowChangeMobile={setShowChangeMobile}
                         setMobileNo={setMobileNo}
                         setParentErrorMessage={setErrorMessage}
                     />}
                </td>
            </tr>
            <tr>
                <th>Email</th>
                <td className="selfcare-account-editable">
                    <span className="text">
                        {customer.customer_contact.email}
                    </span>
                    <IconButton
                        className="selfcare-account-edit-link"
                        component={showChangeEmail ? IconCross : IconEdit}
                        onClick={() => {setShowChangeEmail(!showChangeEmail)}}
                    />
                    {showChangeEmail && isChangingEmailTo === null && <ChangeEmail setIsChangingEmailTo={setIsChangingEmailTo}/>}
                    {showChangeEmail && isChangingEmailTo !== null && <ChangeEmailConfirm isChangingEmailTo={isChangingEmailTo} setIsChangingEmailTo={setIsChangingEmailTo} setShowChangeEmail={setShowChangeEmail}/>}
                </td>
            </tr>
            <tr>
                <th>Kundenummer</th>
                <td>
                    <span className="text">
                        {customer.customer_id}
                    </span>
                </td>
            </tr>
            <tr>
                <th>Kontakt&#173;tilladelser</th>
                <SelfcareContactPermissionsDisplay
                    permissions={customer.customer_permission}
                    permission_text_accepted={customer.customer_permission_text_accepted}/>
            </tr>
            <tr>
                <th>Adresse</th>
                <td>
                    {address(customer.customer_contact)}
                </td>
            </tr>

            <CustomerProductInstanceDisplay customer={customer} />

            <tr>
                <th>Adgangskode</th>
                <td className="selfcare-account-editable">
                    <span className="password">
                        &bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;
                    </span>
                    <IconButton
                        className="selfcare-account-edit-link"
                        component={showChangePassword ? IconCross : IconEdit}
                        onClick={() => {setShowChangePassword(!showChangePassword)}}
                    />
                    {showChangePassword && <ChangePassword />}
                </td>
            </tr>
            </tbody>
        </table>
        </>
    );
}
